import { useMemo } from 'react';
import {
  Category,
  ContentScoreByCategoryData,
  ContentSplineChartData
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import domtoimage from 'src/utils/dom-to-imagev2';
import saveAs from 'file-saver';
import {
  CategoryScoreAdapter,
  VoiceAndToneProductCountAdapter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/CategoryScoreAdapter';
import { KeyMetricsFilterAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { KeyMetric } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { Series } from 'highcharts';
import convertLineChartSeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import { useSnackbar } from 'src/utils/Hooks/typedHooks';
import { VariationsByCategoryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsByCategoryAdapter';

export const defaultImageOption = {
  additionalOptions: {
    scale: 2
  },
  ignoreImages: true
};

const isContentSplineChartData = (maybeData: unknown): maybeData is ContentSplineChartData => {
  if (!maybeData || typeof maybeData !== 'object') {
    return false;
  }

  const { chartData, legendData } = maybeData as ContentSplineChartData;

  if (!chartData || typeof chartData !== 'object' || !legendData || typeof legendData !== 'object') {
    return false;
  }

  if (!Array.isArray(chartData.primaryMetrics) || !Array.isArray(chartData.secondaryMetrics)) {
    return false;
  }

  if (typeof legendData.primaryLegendMetric !== 'number') {
    return false;
  }

  if (typeof legendData.secondaryLegendMetric !== 'number') {
    return false;
  }

  if (typeof legendData.percentChange !== 'number') {
    return false;
  }

  return true;
};

const isBarChartAdapter = (maybeData: unknown): maybeData is CategoryScoreAdapter | VoiceAndToneProductCountAdapter | VariationsByCategoryAdapter => {
  return maybeData instanceof CategoryScoreAdapter || maybeData instanceof VoiceAndToneProductCountAdapter || maybeData instanceof VariationsByCategoryAdapter;
};

const isKeyMetricsAdapter = (maybeAdapter: unknown): maybeAdapter is KeyMetricsFilterAdapter => {
  return maybeAdapter instanceof KeyMetricsFilterAdapter;
};

export const useCsvExporter = (chartName: string, chartContainerRef: React.MutableRefObject<HTMLDivElement>) => {
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { showSnackbar } = useSnackbar();

  const saveImage = async () => {
    const file_name = `${chartName}-${new Date().toISOString()}`;
    try {
      const blob = await domtoimage.toBlob(chartContainerRef.current as HTMLDivElement, defaultImageOption);
      saveAs(blob as Blob, `stackline-chart-${file_name}.png`);
    } catch (e) {
      console.error('Error exporting chart:', e);
    }
  };

  const copyImage = async () => {
    window.focus();
    // Wait for focus and potential rendering delays
    await new Promise((resolve) => setTimeout(resolve, 200));

    const maxWaitTime = 2000;
    const pollInterval = 100;
    const startTime = Date.now();

    while (!chartContainerRef.current && Date.now() - startTime < maxWaitTime) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    // Wait for the ref to become available, with max waiting time of 2 seconds
    if (!chartContainerRef.current) {
      console.error(`Chart container ref not available after ${maxWaitTime}ms`);
      showSnackbar({ type: 'error', message: 'Error copying image: Chart not ready.' });
      return;
    }

    // Ref is available now, proceed with copying
    try {
      const blob = await domtoimage.toBlob(chartContainerRef.current as HTMLDivElement, defaultImageOption);
      if (!blob) {
        console.error(`domtoimage.toBlob returned null for chart: ${chartName}`);
        showSnackbar({ type: 'error', message: 'Failed to generate image for copying.' });
        return;
      }

      const clipboardItem = new ClipboardItem({ 'image/png': blob as Blob });
      await navigator.clipboard.write([clipboardItem]);
      showSnackbar({ type: 'success', message: 'Image copied to clipboard!' });
    } catch (e) {
      console.error(`Error exporting chart ${chartName}to clipboard:`, e);
      // Consider adding a more specific error snackbar here too
      showSnackbar({ type: 'error', message: 'Failed to copy image.' });
    }
  };

  const saveCsvFile = useMemo(
    () => (csvRows: string[], fileName: string) => {
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `${fileName}-${new Date().toISOString()}.csv`);
    },
    []
  );

  const exportSplineChartToCsv = useMemo(
    () => (data: ContentSplineChartData) => {
      try {
        const { chartData } = data;

        // Convert spline data to Highcharts series format
        const series: Series[] = [
          {
            name: mainTimePeriod.displayName,
            data: chartData.primaryMetrics
          },
          {
            name: comparisonTimePeriod.displayName,
            data: chartData.secondaryMetrics
          }
        ];

        // Convert to CSV using existing line chart logic
        const csvContent = convertLineChartSeriesToDelimitedData(
          series,
          false, // isPriorPeriod
          comparisonTimePeriod,
          mainTimePeriod
        );

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${chartName}-spline-chart-${new Date().toISOString()}.csv`);
      } catch (e) {
        console.error('Error exporting spline chart to CSV:', e);
      }
    },
    [chartName, mainTimePeriod, comparisonTimePeriod]
  );

  const exportBarChartToCsv = useMemo(
    () => (adapter: CategoryScoreAdapter | VoiceAndToneProductCountAdapter | VariationsByCategoryAdapter) => {
      const getUniqueCategories = (chartData: ContentScoreByCategoryData[]) => {
        return chartData.reduce((acc, series) => {
          series.categories.forEach((category) => {
            if (!acc.find((c) => c.id === category.id)) {
              acc.push(category);
            }
          });
          return acc;
        }, [] as Category[]);
      };

      try {
        const { chartData, categoryType } = adapter.transform();
        const isAdapterVoiceAndTone = adapter instanceof VoiceAndToneProductCountAdapter;

        const globalCategoryType = isAdapterVoiceAndTone ? 'Entity' : categoryType;
        const csvHeader = [
          `"${globalCategoryType}","${mainTimePeriod.displayName}","${comparisonTimePeriod.displayName}"`
        ];

        const [primaryData, secondaryData] = chartData;
        const currentData = isAdapterVoiceAndTone ? secondaryData : primaryData;
        const comparisonData = isAdapterVoiceAndTone ? primaryData : secondaryData;
        const categories = getUniqueCategories(chartData);

        const rowsWithData = categories.map((category) => {
          const primaryValues = currentData.data.find(([name]) => name === category.name)?.[1] || 0;
          const secondaryValues = comparisonData.data.find(([name]) => name === category.name)?.[1] || 0;

          return {
            category: category.name,
            primaryValue: primaryValues,
            secondaryValue: secondaryValues,
            csvRow: `"${category.name}",${primaryValues},${secondaryValues}`
          };
        });

        // voice and tone is from different class, and we need to sort them differently
        if (isAdapterVoiceAndTone) {
          const voiceOrder = ['Informative', 'Persuasive', 'Sustainable', 'Healthy', 'Playful', 'Supportive'];
          const toneOrder = ['Friendly', 'Luxurious', 'Humorous', 'Encouraging', 'Enthusiastic', 'Confident'];

          const typeOrder = adapter.type === 'voice' ? voiceOrder : toneOrder;
          //
          const sortedRows = typeOrder
            .map((typeName) => rowsWithData.find((row) => row.category === typeName))
            .filter(Boolean);

          const sortedCsvRows = sortedRows.map((row) => row.csvRow);
          saveCsvFile([csvHeader, ...sortedCsvRows], `${chartName}-bar-chart`);
        } else {
          rowsWithData.sort((a, b) => b.primaryValue - a.primaryValue);
          const sortedCsvRows = rowsWithData.map((row) => row.csvRow);
          saveCsvFile([csvHeader, ...sortedCsvRows], `${chartName}-bar-chart`);
        }
      } catch (e) {
        console.error('Error exporting bar chart to CSV:', e);
      }
    },
    [mainTimePeriod.displayName, comparisonTimePeriod.displayName, chartName, saveCsvFile]
  );

  const exportKeyMetricsToCsv = useMemo(
    () => (adapter: KeyMetricsFilterAdapter) => {
      const buildCsvRow = (weekEnding: string, overallScore: number, metrics: KeyMetric[]) => {
        return [`"${weekEnding}"`, overallScore, ...metrics.map((metric) => metric.value)];
      };
      try {
        const { primaryOverallScore, secondaryOverallScore, primaryData, secondaryData } = adapter.transform();
        const csvHeader = ['WeekEnding', 'OverallScore', ...primaryData.map((metric) => metric.name)];
        const csvRows = [];

        csvRows.push(buildCsvRow(mainTimePeriod.displayName, primaryOverallScore, primaryData));
        csvRows.push(buildCsvRow(comparisonTimePeriod.displayName, secondaryOverallScore, secondaryData));

        saveCsvFile([csvHeader, ...csvRows], `${chartName}-key-metrics`);
      } catch (e) {
        console.error('Error exporting key metrics to CSV:', e);
      }
    },
    [mainTimePeriod.displayName, comparisonTimePeriod.displayName, chartName, saveCsvFile]
  );

  const exportCsv = useMemo(
    () => (data: ContentSplineChartData | CategoryScoreAdapter | KeyMetricsFilterAdapter | VariationsByCategoryAdapter) => {
      if (isContentSplineChartData(data)) {
        exportSplineChartToCsv(data);
      } else if (isBarChartAdapter(data)) {
        exportBarChartToCsv(data);
      } else if (isKeyMetricsAdapter(data)) {
        exportKeyMetricsToCsv(data);
      } else {
        console.error('Invalid data type for exportCsv');
      }
    },
    [exportSplineChartToCsv, exportBarChartToCsv, exportKeyMetricsToCsv]
  );

  return { saveImage, copyImage, exportCsv };
};
